/**
 * Observe an element's size, running `callback` on the next animation frame.
 *
 * Chrome reports "ResizeObserver loop completed with undelivered notifications"
 * when a ResizeObserver callback mutates layout in the same frame. Scheduling
 * the work onto rAF lets the current frame's notifications finish first.
 */
export function observeElementResize(
  element: Element,
  callback: ResizeObserverCallback,
): () => void {
  let frame = 0;
  const observer = new ResizeObserver((entries, obs) => {
    if (frame) cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      frame = 0;
      callback(entries, obs);
    });
  });
  observer.observe(element);
  return () => {
    if (frame) cancelAnimationFrame(frame);
    observer.disconnect();
  };
}

/**
 * Match the canvas bitmap to its CSS box × device pixel ratio.
 * Returns true when the bitmap size changed (the caller should redraw).
 *
 * Assigning `canvas.width` is a layout mutation: without a CSS size lock it
 * grows the element (intrinsic bitmap size), and even with one some browsers
 * re-notify. Skipping no-op writes breaks that loop.
 */
export function syncCanvasBitmapSize(canvas: HTMLCanvasElement): boolean {
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  if (!w || !h) return false;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const nextW = Math.round(w * dpr);
  const nextH = Math.round(h * dpr);
  if (canvas.width === nextW && canvas.height === nextH) return false;
  canvas.width = nextW;
  canvas.height = nextH;
  return true;
}
